import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

const TeamTable = ({ apiUrl }) => {
    const [allTeams, setAllTeams] = useState([]);
    const [filteredTeams, setFilteredTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // State for filters
    const [selectedLeague, setSelectedLeague] = useState('');
    const [selectedConference, setSelectedConference] = useState('');
    const [selectedDivision, setSelectedDivision] = useState('');

    // State for dropdown options
    const [leagueOptions, setLeagueOptions] = useState([]);
    const [conferenceOptions, setConferenceOptions] = useState([]);
    const [divisionOptions, setDivisionOptions] = useState([]);

    useEffect(() => {
        fetch(apiUrl)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                const teamsWithIds = data.map((team, index) => ({
                    ...team,
                    id: team.team_name || team.id || index, // Use team_name, then team.id, then index as fallback for id
                }));
                setAllTeams(teamsWithIds);
                setFilteredTeams(teamsWithIds);

                // Populate dropdown options
                setLeagueOptions([...new Set(teamsWithIds.map(t => t.league).filter(Boolean))].sort());
                setConferenceOptions([...new Set(teamsWithIds.map(t => t.conference).filter(Boolean))].sort());
                setDivisionOptions([...new Set(teamsWithIds.map(t => t.division).filter(Boolean))].sort());

                setLoading(false);
            })
            .catch(error => {
                console.error("Error fetching data:", error);
                setError(error);
                setLoading(false);
            });
    }, [apiUrl]);

    useEffect(() => {
        let tempFilteredTeams = allTeams;

        if (searchTerm) {
            tempFilteredTeams = tempFilteredTeams.filter(team =>
                team.team_name?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        if (selectedLeague) {
            tempFilteredTeams = tempFilteredTeams.filter(t => t.league === selectedLeague);
        }
        if (selectedConference) {
            tempFilteredTeams = tempFilteredTeams.filter(t => t.conference === selectedConference);
        }
        if (selectedDivision) {
            tempFilteredTeams = tempFilteredTeams.filter(t => t.division === selectedDivision);
        }
        setFilteredTeams(tempFilteredTeams);
    }, [searchTerm, allTeams, selectedLeague, selectedConference, selectedDivision]);

    const columns = [
        { field: 'team_name', headerName: 'Team Name', width: 200 },
        { field: 'league', headerName: 'League', width: 120 },
        { field: 'conference', headerName: 'Conference', width: 150 },
        { field: 'division', headerName: 'Division', width: 120 },
        { field: 'total_tpe', headerName: 'Total TPE', type: 'number', width: 130 },
        { field: 'player_count', headerName: 'Player Count', type: 'number', width: 130 },
        { field: 'average_tpe', headerName: 'Average TPE', type: 'number', width: 150 },
    ];

    if (loading) {
        return <div>Loading teams...</div>;
    }

    if (error) {
        return <div>Error: {error.message}</div>;
    }

    const createDropdown = (label, value, onChange, options) => {
        // Using a consistent ID generation for the label
        const labelId = `${label.toLowerCase().replace(/\s+/g, '-')}-select-label`;

        return (
            <Grid item xs={12} sm={6} md={4} lg={3}>
                <FormControl fullWidth variant="outlined">
                    {/* Add shrink={true} to the InputLabel */}
                    <InputLabel id={labelId} shrink={true}>
                        {label}
                    </InputLabel>
                    <Select
                        labelId={labelId} // Associates Select with InputLabel
                        displayEmpty
                        value={value}
                        onChange={onChange}
                        label={label} // Still needed for the outlined variant's notch
                        sx={{ width: '100%' }}
                    >
                        <MenuItem value="">
                            <em>All {label}s</em>
                        </MenuItem>
                        {options.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </Select>
                </FormControl>
            </Grid>
        );
    };

    return (
        <div>
            <Box sx={{ mb: 2, p: 2, border: '1px solid lightgray', borderRadius: '4px' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <TextField
                            label="Search by Team Name"
                            variant="outlined"
                            fullWidth
                            // No size="small" for larger default size
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </Grid>
                    {createDropdown("League", selectedLeague, (e) => setSelectedLeague(e.target.value), leagueOptions)}
                    {createDropdown("Conference", selectedConference, (e) => setSelectedConference(e.target.value), conferenceOptions)}
                    {createDropdown("Division", selectedDivision, (e) => setSelectedDivision(e.target.value), divisionOptions)}
                </Grid>
            </Box>
            <div style={{ height: 700, width: '100%' }}>
                <DataGrid
                    rows={filteredTeams}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableSelectionOnClick
                    initialState={{
                        sorting: {
                            sortModel: [{ field: 'total_tpe', sort: 'desc' }],
                        },
                    }}
                />
            </div>
        </div>
    );
};

export default TeamTable;